import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { IUsernameBody } from '@/providers/Username.provider';

import { ApiTagsEnum } from '@/enums/apiTags.enum';

import { baseQuery } from '@/utils/api.util';

import {
  ContestType,
  GetContestLeaderboardRes,
  GetContestListRes,
  GetLiveContestRes,
  GetOneContestRes,
  GetWeeklyStatsParams,
  GetWeeklyStatsRes,
  LoginReqBody,
  LoginResponse,
  MakeSelectionsBody,
} from '@/types/domain';

interface BaseRes {
  success: boolean;
}

export const api = createApi({
  reducerPath: 'mainApi',
  tagTypes: Object.values(ApiTagsEnum),
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginReqBody>({
      query: (body) => ({
        url: '/login',
        method: 'POST',
        body,
      }),
    }),
    getContestsList: builder.query<
      ContestType[],
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({ url: '/contests/active', params }),
      providesTags: [ApiTagsEnum.GET_ALL_CONTESTS],
      transformResponse: (response: GetContestListRes) => response.contests,
    }),

    getOneContest: builder.query<GetOneContestRes, { id: string }>({
      query: ({ id }) => `/contests/${id}`,
      providesTags: [ApiTagsEnum.GET_ONE_CONTEST],
    }),
    getLiveContest: builder.query<GetLiveContestRes, void>({
      query: () => '/live-contest',
      providesTags: [ApiTagsEnum.GET_LIVE_CONTEST],
    }),
    getWeeklyStats: builder.query<GetWeeklyStatsRes, GetWeeklyStatsParams>({
      query: ({ contest_week_id, week_id }) =>
        `/contests/${contest_week_id}/weeks/${week_id}/stats`,
      providesTags: [ApiTagsEnum.GET_WEEKLY_STATS],
    }),
    getContestLeaderboard: builder.query<
      GetContestLeaderboardRes,
      { id: string }
    >({
      query: ({ id }) => `/contests/${id}/leaderboard`,
      providesTags: [ApiTagsEnum.GET_CONTEST_LEADERBOARD],
    }),
    makeSelections: builder.mutation<BaseRes, MakeSelectionsBody>({
      query: ({ contest_id, ...body }) => ({
        url: `/contests/${contest_id}/selections`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        ApiTagsEnum.GET_ONE_CONTEST,
        ApiTagsEnum.GET_ALL_CONTESTS,
      ],
    }),
    postContestEnter: builder.mutation<BaseRes, any>({
      query: ({ contest_id, ...body }) => ({
        url: `/contests/${contest_id}/enter`,
        method: 'POST',
        body,
      }),
    }),
    getMe: builder.query<any, void>({
      query: () => '/me',
    }),
    updateMe: builder.mutation<BaseRes, IUsernameBody>({
      query: (body) => ({ url: '/me', method: 'PUT', body }),
    }),
    getPickemRules: builder.query<any, { id: string; lang: string }>({
      query: ({ id, lang }) => ({
        url: `/page/contest-${id}?lang=${lang}`,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetContestsListQuery,
  useGetOneContestQuery,
  useGetLiveContestQuery,
  useMakeSelectionsMutation,
  useGetWeeklyStatsQuery,
  useGetContestLeaderboardQuery,
  usePostContestEnterMutation,
  useGetMeQuery,
  useUpdateMeMutation,
  useGetPickemRulesQuery,
} = api;
